import { DialogComponent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {object} FilterDrawerRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLButtonElement} [trigger]
 * @property {HTMLElement} [results]
 */

/**
 * @extends {DialogComponent}
 */
class FilterDrawer extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'trigger', 'applyButton'];

  #pendingCount = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#registerEvents();
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.addEventListener('dialog:close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unregisterEvents();
    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.removeEventListener('dialog:close', this.#handleDialogClose);
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  #handleDialogOpen = () => {
    this.#trigger?.setAttribute('aria-expanded', 'true');
  };

  #handleDialogClose = () => {
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#trigger?.focus();
  };

  /** @type {(event: Event) => void} */
  #onFormChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.type !== 'checkbox' && target.type !== 'radio' && target.type !== 'number') {
      return;
    }

    if (target.type === 'checkbox' || target.type === 'radio') {
      const href = target.checked ? target.dataset.urlToAdd : target.dataset.urlToRemove;

      if (href) {
        this.#renderFromURL(new URL(href, window.location.origin));
        return;
      }
    }

    const form = this.refs.form;
    if (!(form instanceof HTMLFormElement)) return;

    this.#submitForm(form);
  };

  /** @type {(event: Event) => void} */
  #onFormSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;
    this.#submitForm(form);
  };

  #onPopState = () => {
    this.#renderFromURL(new URL(window.location.href), { updateHistory: false });
  };

  /**
   * @param {{ href?: string, reopen?: boolean }} data
   * @param {Event} [event]
   */
  handleNavigate(data, event) {
    event?.preventDefault();
    if (!data || !data.href) return;
    const reopen = typeof data.reopen === 'boolean' ? data.reopen : undefined;
    this.#renderFromURL(new URL(data.href, window.location.origin), { reopen });
  }

  /**
   * @param {Event} [event]
   */
  applyAndClose(event) {
    event?.preventDefault();
    const form = this.refs.form;
    if (!(form instanceof HTMLFormElement)) return;
    this.#submitForm(form, { reopen: false });
  }

  openDrawer = () => {
    this.showDialog();
  };

  closeDrawer = () => {
    this.closeDialog();
  };

  #registerEvents() {
    const form = this.refs.form;
    if (form instanceof HTMLFormElement) {
      form.addEventListener('change', this.#onFormChange);
      form.addEventListener('submit', this.#onFormSubmit);
    }
    window.addEventListener('popstate', this.#onPopState);
  }

  #unregisterEvents() {
    const form = this.refs.form;
    if (form instanceof HTMLFormElement) {
      form.removeEventListener('change', this.#onFormChange);
      form.removeEventListener('submit', this.#onFormSubmit);
    }
    window.removeEventListener('popstate', this.#onPopState);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {{ reopen?: boolean }} [options]
   */
  async #submitForm(form, options = {}) {
    if (!(form instanceof HTMLFormElement)) return;
    const params = new URLSearchParams();
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
      if (key === 'page') continue;
      if (typeof value !== 'string') continue;
      if (value.trim() === '') continue;
      params.append(key, value);
    }

    if (options.reopen === false && this.refs.dialog.open) {
      await this.#waitForClose();
    }

    const url = new URL(window.location.href);
    url.search = params.toString();
    await this.#renderFromURL(url, { reopen: options.reopen });
  }

  /**
   * @param {URL} url
   * @param {{ updateHistory?: boolean, reopen?: boolean }} [options]
   */
  async #renderFromURL(url, { updateHistory = true, reopen } = {}) {
    if (!this.sectionId) return;

    const reopenDrawer = typeof reopen === 'boolean' ? reopen : this.refs.dialog.open;
    this.#pendingCount++;
    this.#setLoading(true);

    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: 'hydration',
        url,
      });

      const cleanUrl = new URL(url);
      cleanUrl.searchParams.delete('section_id');

      if (updateHistory) {
        window.history.pushState({}, '', cleanUrl);
      }

      if (reopenDrawer) {
        requestAnimationFrame(() => {
          if (!this.refs.dialog.open) {
            this.showDialog({ immediate: true });
          }
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    } finally {
      this.#pendingCount--;
      if (this.#pendingCount === 0) {
        this.#setLoading(false);
      }
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const isBusy = Boolean(isLoading);
    this.toggleAttribute('data-loading', isBusy);

    const applyButton = this.refs.applyButton;
    if (applyButton instanceof HTMLButtonElement) {
      applyButton.setAttribute('aria-busy', String(isBusy));
    }
  }

  async #waitForClose() {
    if (!this.refs.dialog.open) return;
    /** @type {Promise<void>} */
    const promise = new Promise((resolve) => {
      const dialog = this.refs.dialog;
      const handle = () => {
        dialog.removeEventListener('close', handle);
        resolve();
      };
      dialog.addEventListener('close', handle, { once: true });
      this.closeDialog();
    });
    await promise;
  }

  get #trigger() {
    return this.refs.trigger instanceof HTMLButtonElement ? this.refs.trigger : null;
  }
}

if (!customElements.get('filter-drawer')) {
  customElements.define('filter-drawer', FilterDrawer);
}
